// ============================================================
// TILT — APP
// ------------------------------------------------------------
// The router. Screen state + a goTo/goBack pair, exactly the
// shape Terror's App.js uses (harvested pattern, Tier 2). No
// react-router — a single-page arcade cabinet doesn't need URLs.
//
// ── WHAT CHANGED (ROADMAP P1) ────────────────────────────────
// App.js no longer knows what localStorage is. It used to hold
// the save shape, the JSON, the key, and the write. Now it holds
// a state object and calls `progression.commitAll(prev, [deltas])`.
//
// Whether that lands in localStorage or in RTDB is progression.js's
// business. Swapping to Firebase (P1.2) does not touch this file
// AT ALL — it's one setDriver() call at boot, after rules ship.
//
// This is the same shape as SabotageBus: one entry point, a
// swappable driver behind it. That pattern has now paid for
// itself twice.
//
// ⚠ The only writes in this file are DELTAS. There is no code
//   path here that can overwrite a save wholesale, because there
//   is no function available to it that could. (The holo-wipe.)
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { PAGE_BG, ensureKeyframes } from "./theme";
import { validateLoadout } from "./Powers";
import { setSocialHidden } from "./ieSocial";   // the friends pill steps aside while a table is live
// ★ CONTINUITY LAW: the Cirque de J'ynx is the SAME table in every
// mode. The duel runs the exact FOUNDRY module solo runs — same
// geometry, same boss, same ladder. (Spite remains in the registry
// as a solo table; map selection/voting is a later feature.)
import { FOUNDRY } from "./tables";
import { TitleScreen, TableSelect, LoadoutScreen, GauntletScreen } from "./screens/Menus";
import SoloScreen from "./screens/SoloScreen";
import DuelScreen from "./screens/DuelScreen";
import RelayScreen from "./screens/RelayScreen";
// QUICK CO-OP (2026-10-25): one soul (ieIdentity), a seat on a Tilt server (ieServers, vendored from the
// trunk byte-identical), and the relay itself (tiltRelay.js + tiltRelayShell.js). Signed out: none of this runs.
import { useIEAuth } from "./ieIdentity";
import { useServer, ServerChoice } from "./ieServers";
import { useRelay } from "./tiltRelayShell";
import { GAME_ID } from "./tiltOnline";
import { UI } from "./theme";
import {
  EMPTY, read, commitAll, readHandoffUid, TOKENS,
  dBest, dCleared, dGauntlet, dLoadout, dTokens, dStat,
} from "./progression";

export default function App() {
  ensureKeyframes();

  const [screen, setScreen]         = useState("title");
  const [prevScreen, setPrevScreen] = useState("title");
  const [table, setTable]           = useState(null);
  const [opponent, setOpponent]     = useState(null);

  // The save starts EMPTY and is filled by an async read. This is
  // deliberately async even on the local driver — because the
  // Firebase driver WILL be async, and a screen that only works
  // when the read is instant is a screen that breaks on deploy day.
  // Exercise the async path in single-player. Always.
  const [save, setSave]   = useState(EMPTY);
  const [ready, setReady] = useState(false);

  // ── QUICK CO-OP ── (the MarkCorner owns signing in; this only reads who you are)
  const auth = useIEAuth({ gameId: GAME_ID });
  const signedIn = !!(auth.user && !auth.user.isAnonymous && auth.authUid && auth.writable && auth.phase === "ready");
  const server = useServer({ game: GAME_ID, uid: auth.authUid, name: auth.name, enabled: signedIn });
  const seated = signedIn && !!server.sid;
  const relay = useRelay({ sid: seated ? server.sid : null, uid: seated ? auth.authUid : null, name: auth.name, seats: server.seats });
  const [relayBall, setRelayBall] = useState(null);     // { before } while I'm shooting my ball

  useEffect(() => {
    // P1.4 — cross-app identity handoff. Pier routes into TILT with
    // ?uid=UID. Read it, strip it from the URL, hold it for the
    // Firebase driver. Harmless no-op on the local driver today.
    readHandoffUid();

    let alive = true;
    read().then((s) => {
      if (!alive) return;
      setSave(s);
      setReady(true);
    });
    return () => { alive = false; };
  }, []);

  /** THE ONLY WRITE PATH. Everything goes through here, as deltas. */
  const push = useCallback((...deltas) => {
    setSave((prev) => {
      // Optimistic: apply locally now, let the driver reconcile.
      // A player who cleared a trial and then lost wifi still
      // cleared the trial. The UI must never wait on a network.
      const next = deltas.reduce((acc, d) => d(acc), prev);
      commitAll(prev, deltas);   // fire-and-forget; driver handles failure
      return next;
    });
  }, []);

  // THE FRIENDS PILL (index.js) would sit on the playfield and the touch flippers - hide it while a table is live.
  useEffect(() => { setSocialHidden(screen === "solo" || screen === "duel" || screen === "relay_ball"); return () => setSocialHidden(false); }, [screen]);

  const goTo = (next) => { setPrevScreen(screen); setScreen(next); };
  const goBack = () => { setScreen(prevScreen); setPrevScreen("title"); };

  // ── Results ────────────────────────────────────────────────
  // One event = ONE commit. A finished trial is a single fact
  // (score + cleared + payout), not three racing writes.
  // ── D6: fold a game's stat counts into the SAME commit as its
  //    result. dStat is schema-guarded (an unknown key is a no-op,
  //    same posture as the rules' whitelist) so a screen sending a
  //    stray key can never poison the write.
  const statDeltas = (stats) =>
    Object.entries(stats || {})
      .filter(([, n]) => n > 0)
      .map(([k, n]) => dStat(k, n));

  const onSoloResult = useCallback(({ table: tid, score, cleared, stats }) => {
    const deltas = [
      dBest(tid, score),
      dTokens(TOKENS.perScore(score)),
      ...statDeltas(stats),
    ];
    if (cleared) {
      deltas.push(dCleared(tid));
      // Only pay the trial bounty ONCE — dCleared is append-only, so
      // check BEFORE it lands. Re-clearing a trial you've already
      // beaten is fine; farming it for tokens is not.
      if (!save.cleared[tid]) deltas.push(dTokens(TOKENS.trialCleared));
    }
    push(...deltas);
  }, [push, save.cleared]);

  const onDuelResult = useCallback(({ won, opponent: oid, stats }) => {
    // Stats are earned by PLAYING, not by winning — a loss still
    // launched balls and ate sabotages. Commit them either way.
    const sd = statDeltas(stats);
    if (!won || !oid) {
      if (sd.length) push(...sd);
      return;
    }
    const first = !save.gauntlet[oid];
    push(
      dGauntlet(oid),
      dTokens(first ? TOKENS.gauntletWin : TOKENS.duelWin),
      ...sd,
    );
  }, [push, save.gauntlet]);

  // ── Boot gate ──────────────────────────────────────────────
  // One frame of black beats one frame of a WRONG loadout.
  if (!ready) {
    return <div style={{ minHeight: "100vh", background: PAGE_BG }} />;
  }

  // ── Routes ─────────────────────────────────────────────────
  if (screen === "title") {
    return (
      <TitleScreen
        tokens={save.tiltTokens}
        onPlay={() => goTo("select_score")}
        onTrials={() => goTo("select_trial")}
        onDuel={() => goTo("gauntlet")}
        onLoadout={() => goTo("loadout")}
        onCoop={() => goTo("relay")}
      />
    );
  }

  if (screen === "relay") {
    return (
      <>
        <RelayScreen
          signedIn={signedIn} seated={seated} relay={relay}
          onShoot={async () => { const b = await relay.shoot(); if (b) { setRelayBall(b); setScreen("relay_ball"); } }}
          onBack={() => setScreen("title")}
        />
        <ServerChoice server={server} theme={{ accent: UI.brassBright, ink: UI.text, dim: UI.textDim, bg: UI.pageDeep, panel: UI.panel, font: UI.font, display: UI.fontD, z: 900 }} />
      </>
    );
  }

  if (screen === "relay_ball" && relayBall) {
    return (
      <SoloScreen
        key={`relay_${relayBall.turnSeq}`}
        table={FOUNDRY}
        loadout={validateLoadout(save.loadout)}
        relay={{
          before: relayBall.before,
          onBallOver: (snap) => { relay.finish(snap, relayBall); setRelayBall(null); setScreen("relay"); },
        }}
        onExit={() => { setRelayBall(null); setScreen("relay"); }}
      />
    );
  }

  if (screen === "select_score") {
    return (
      <TableSelect
        kind="score" title="SCORE TABLES"
        best={save.best}
        onPick={(t) => { setTable(t); goTo("solo"); }}
        onBack={goBack}
      />
    );
  }

  if (screen === "select_trial") {
    return (
      <TableSelect
        kind="trial" title="TRIALS"
        best={save.best}
        cleared={save.cleared}
        onPick={(t) => { setTable(t); goTo("solo"); }}
        onBack={goBack}
      />
    );
  }

  if (screen === "loadout") {
    return (
      <LoadoutScreen
        loadout={save.loadout}
        onSave={(l) => { push(dLoadout(l)); goBack(); }}
        onBack={goBack}
      />
    );
  }

  if (screen === "gauntlet") {
    return (
      <GauntletScreen
        cleared={save.gauntlet}
        onPick={(n) => { setOpponent(n); setTable(FOUNDRY); goTo("duel"); }}
        onBack={goBack}
      />
    );
  }

  if (screen === "solo" && table) {
    // A trial table's loadout is FIXED — the puzzle IS the kit.
    const kit = table.kind === "trial" ? table.loadout : validateLoadout(save.loadout);
    return (
      <SoloScreen
        key={table.id}
        table={table}
        loadout={kit}
        best={save.best[table.id] || 0}
        onResult={onSoloResult}
        onExit={() => { setTable(null); setScreen("title"); }}
      />
    );
  }

  if (screen === "duel" && table && opponent) {
    return (
      <DuelScreen
        key={`${table.id}_${opponent.id}`}
        table={table}
        loadout={validateLoadout(save.loadout)}
        opponent={opponent}
        onResult={onDuelResult}
        onExit={() => { setOpponent(null); setTable(null); setScreen("gauntlet"); }}
      />
    );
  }

  return <div style={{ minHeight: "100vh", background: PAGE_BG }} />;
}
